import React, { useState, useEffect } from 'react';
import { FaUserCheck, FaUserClock, FaUserSlash } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend
} from 'chart.js';
import DateRangeFilter from '../components/DateRangeFilter';
import LoadingOverlay from '../components/LoadingOverlay';
import { fetchUserRegistrationsPerDay, fetchUserStatistics } from '../services/api';
import { fillDateGaps } from '../utils/dataGrouping';
import { formatCompactNumber } from '../utils/formatters';
import './DataPages.css';
import './Pages.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AccountsOverview = ({ isDarkMode }) => {
  const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29);
    return {
      startDate: toUTCDateStr(start),
      endDate: toUTCDateStr(end)
    };
  };

  const [startDate, setStartDate] = useState(getDefaultRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultRange().endDate);
  const [data, setData] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [userStats, setUserStats] = useState({ activeUsers: 0, inactiveUsers: 0, deletedUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadData = async (start, end, isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const [result, stats] = await Promise.all([
        fetchUserRegistrationsPerDay(start, end),
        fetchUserStatistics(start, end)
      ]);
      setData(fillDateGaps(result.items || []));
      if (result.earliestDate) setEarliestDate(result.earliestDate);
      if (result.latestDate) setLatestDate(result.latestDate);
      setUserStats({
        activeUsers: stats.activeUsers || 0,
        inactiveUsers: stats.inactiveUsers || 0,
        deletedUsers: stats.deletedUsers || 0
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const { startDate: start, endDate: end } = getDefaultRange();
    loadData(start, end, false);
  }, []);

  const handleApply = (directStartDate = null, directEndDate = null) => {
    const fetchStartDate = directStartDate ?? startDate;
    const fetchEndDate = directEndDate ?? endDate;
    loadData(fetchStartDate, fetchEndDate, true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const textColor = isDarkMode ? '#e5e7eb' : '#333333';
  const totalAccounts = data.reduce((sum, item) => sum + item.count, 0);

  const getPeriodLabel = () => {
    if (!startDate || !endDate) return 'All Time';
    const s = new Date(startDate);
    const e = new Date(endDate);
    const days = Math.ceil(Math.abs(e - s) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} Days`;
  };

  const chartData = {
    labels: data.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Accounts Created',
      data: data.map(item => item.count),
      backgroundColor: 'rgba(175, 62, 255, 0.8)',
      borderColor: '#af3eff',
      borderWidth: 2,
      borderRadius: 8,
      hoverBackgroundColor: '#af3eff',
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(51, 51, 51, 0.95)',
        padding: 12,
        titleColor: '#fff',
        bodyColor: '#fff',
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
              const dayName = new Date(label + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
              return [label, dayName];
            }
            return label;
          },
          label: (context) => `Accounts: ${context.parsed.y}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: textColor, font: { size: 12 } },
        grid: { color: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }
      },
      x: {
        ticks: { color: textColor, font: { size: 11 }, maxRotation: 45 },
        grid: { display: false }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  return (
    <div className="page-container">
      {refreshing && <LoadingOverlay />}

      <div className="page-header">
        <div className="page-header-content">
          <h1>Accounts Overview</h1>
          <div className="page-header-badges">
            <div className="control-badge">
              <span className="badge-label">Period</span>
              <span className="badge-value">{getPeriodLabel()}</span>
            </div>
            {!loading && (
              <div className="control-badge">
                <span className="badge-label">Created</span>
                <span className="badge-value">{totalAccounts.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="data-page-content">
        <div className="filters-container">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={handleApply}
            earliestDate={earliestDate}
            latestDate={latestDate}
            showMax={true}
          />
        </div>

        {!loading && !error && (
          <div className="summary-container">
            <div className="summary-card card-1">
              <div className="card-icon">
                <FaUserCheck size={24} />
              </div>
              <div className="card-content">
                <h3>Active</h3>
                <p className="card-value" title={userStats.activeUsers.toLocaleString()}>{formatCompactNumber(userStats.activeUsers)}</p>
              </div>
            </div>

            <div className="summary-card card-2">
              <div className="card-icon">
                <FaUserClock size={24} />
              </div>
              <div className="card-content">
                <h3>Inactive</h3>
                <p className="card-value" title={userStats.inactiveUsers.toLocaleString()}>{formatCompactNumber(userStats.inactiveUsers)}</p>
              </div>
            </div>

            <div className="summary-card card-3">
              <div className="card-icon">
                <FaUserSlash size={24} />
              </div>
              <div className="card-content">
                <h3>Deleted</h3>
                <p className="card-value" title={userStats.deletedUsers.toLocaleString()}>{formatCompactNumber(userStats.deletedUsers)}</p>
              </div>
            </div>
          </div>
        )}

        {error && <div className="table-empty">Error: {error}</div>}
        {loading && <div className="table-loading">Loading ...</div>}

        {!loading && !error && data.length > 0 && (
          <div className="data-page-chart-card">
            <div className="chart-header">
              <h3>Accounts Created Per Day</h3>
              <span className="chart-badge">
                {formatCompactNumber(totalAccounts)} accounts
              </span>
            </div>
            <div className="chart-wrapper">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="table-empty">
            No accounts data for the selected period.
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsOverview;
